/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/contexts/PlatformAdminContext.tsx
 * PURPOSE: Platform admin authentication context with dual auth support
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: Frontend Application, Vietnamese F&B Platform
 * - Impact: Application Quality, User Experience, Performance
 * - Stakeholders: Development Team, End Users, Product Team
 *
 * @technical-architecture
 * - Layer: Frontend Application Layer
 * - Pattern: React Application, TypeScript, Modern Frontend
 * - Dependencies: React 18, TypeScript, Vite, TailwindCSS
 */

import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { platformAdminApi, PlatformAdminAuthResponse } from '../services/api/platform-admin/platformAdmin';

// Types
interface PlatformAdmin {
  id: string;
  email: string;
  username: string;
  full_name: string;
  role: string;
  is_active: boolean;
  is_verified: boolean;
}

interface PlatformAdminAuth {
  admin: PlatformAdmin | null;
  accessToken: string | null;
  refreshToken: string | null;
  tokenType: string | null;
}

interface PlatformAdminContextType {
  // State
  platformAdmin: PlatformAdminAuth;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;  // Sprint 17 P0 Fix: Track initialization state
  error: string | null;

  // Actions
  setPlatformAdmin: (auth: PlatformAdminAuthResponse) => void;
  logout: () => void;
  clearError: () => void;
  refreshTokens: () => Promise<void>;
}

// Action types
type PlatformAdminAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_PLATFORM_ADMIN'; payload: PlatformAdminAuthResponse }
  | { type: 'CLEAR_PLATFORM_ADMIN' }
  | { type: 'UPDATE_TOKENS'; payload: { accessToken: string; refreshToken: string } }
  | { type: 'SET_INITIALIZED' };  // Sprint 17 P0 Fix: Mark initialization complete

// Initial state - Sprint 17 P0 Fix: isLoading starts true to prevent auth check before initialization
const initialState = {
  platformAdmin: {
    admin: null,
    accessToken: null,
    refreshToken: null,
    tokenType: null,
  },
  isAuthenticated: false,
  isLoading: true,  // Start with true to prevent redirect before localStorage check
  isInitialized: false,  // Track if we've completed initialization
  error: null,
};

// Reducer
function platformAdminReducer(state: typeof initialState, action: PlatformAdminAction): typeof initialState {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
        error: action.payload ? null : state.error, // Clear error when starting new operation
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case 'SET_PLATFORM_ADMIN':
      return {
        ...state,
        platformAdmin: {
          admin: action.payload.admin,
          accessToken: action.payload.access_token,
          refreshToken: action.payload.refresh_token,
          tokenType: action.payload.token_type,
        },
        isAuthenticated: true,
        isLoading: false,
        isInitialized: true,  // Sprint 17 P0 Fix: Mark as initialized when auth is set
        error: null,
      };

    case 'CLEAR_PLATFORM_ADMIN':
      return {
        ...initialState,
        isLoading: false,  // Sprint 17 P0 Fix: After logout, we're initialized but not loading
        isInitialized: true,
      };

    case 'SET_INITIALIZED':
      return {
        ...state,
        isLoading: false,
        isInitialized: true,
      };

    case 'UPDATE_TOKENS':
      return {
        ...state,
        platformAdmin: {
          ...state.platformAdmin,
          accessToken: action.payload.accessToken,
          refreshToken: action.payload.refreshToken,
        },
      };

    default:
      return state;
  }
}

// Context
const PlatformAdminContext = createContext<PlatformAdminContextType | undefined>(undefined);

// Storage keys
const STORAGE_KEYS = {
  PLATFORM_ADMIN: 'platform_admin_auth',
  ACCESS_TOKEN: 'platform_admin_access_token',
  REFRESH_TOKEN: 'platform_admin_refresh_token',
} as const;

// Provider component
interface PlatformAdminProviderProps {
  children: ReactNode;
}

export const PlatformAdminProvider: React.FC<PlatformAdminProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(platformAdminReducer, initialState);

  // Initialize from localStorage on mount - Sprint 17 P0 Fix: Properly handle initialization timing
  useEffect(() => {
    const initializeAuth = async () => {
      const storedAuth = localStorage.getItem(STORAGE_KEYS.PLATFORM_ADMIN);
      const storedAccessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const storedRefreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

      if (storedAuth && storedAccessToken && storedRefreshToken) {
        try {
          const adminData = JSON.parse(storedAuth);

          // Check if token is expired
          if (!platformAdminApi.isTokenExpired(storedAccessToken)) {
            dispatch({
              type: 'SET_PLATFORM_ADMIN',
              payload: {
                admin: adminData,
                access_token: storedAccessToken,
                refresh_token: storedRefreshToken,
                token_type: 'bearer',
              },
            });
          } else {
            // Try to refresh token - if fails, will logout and set initialized
            try {
              await refreshTokens();
            } catch {
              dispatch({ type: 'SET_INITIALIZED' });
            }
          }
        } catch (error) {
          console.warn('Failed to parse stored auth:', error);
          logout();
        }
      } else {
        // Sprint 17 P0 Fix: No stored auth - mark as initialized so dashboard can redirect properly
        dispatch({ type: 'SET_INITIALIZED' });
      }
    };

    initializeAuth();
  }, []);

  // Persist to localStorage whenever auth state changes
  useEffect(() => {
    if (state.isAuthenticated && state.platformAdmin.admin && state.platformAdmin.accessToken) {
      localStorage.setItem(STORAGE_KEYS.PLATFORM_ADMIN, JSON.stringify(state.platformAdmin.admin));
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, state.platformAdmin.accessToken);

      if (state.platformAdmin.refreshToken) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, state.platformAdmin.refreshToken);
      }
    } else {
      // Clear storage when not authenticated
      localStorage.removeItem(STORAGE_KEYS.PLATFORM_ADMIN);
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    }
  }, [state.isAuthenticated, state.platformAdmin]);

  // Actions
  const setPlatformAdmin = (auth: PlatformAdminAuthResponse) => {
    dispatch({ type: 'SET_PLATFORM_ADMIN', payload: auth });
  };

  const logout = () => {
    dispatch({ type: 'CLEAR_PLATFORM_ADMIN' });
  };

  const clearError = () => {
    dispatch({ type: 'SET_ERROR', payload: null });
  };

  const refreshTokens = async () => {
    if (!state.platformAdmin.refreshToken) {
      logout();
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const response = await platformAdminApi.refresh(state.platformAdmin.refreshToken);

      dispatch({
        type: 'SET_PLATFORM_ADMIN',
        payload: response,
      });
    } catch (error: any) {

      dispatch({ type: 'SET_ERROR', payload: 'Session expired. Please login again.' });
      logout();
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Auto-refresh tokens when they're about to expire (5 minutes before expiry)
  useEffect(() => {
    if (!state.platformAdmin.accessToken || !state.isAuthenticated) return;

    const checkAndRefreshToken = async () => {
      try {
        const payload = JSON.parse(atob(state.platformAdmin.accessToken!.split('.')[1]));
        const expiryTime = payload.exp * 1000;
        const currentTime = Date.now();
        const timeUntilExpiry = expiryTime - currentTime;

        // Refresh if token expires in less than 5 minutes
        if (timeUntilExpiry < 5 * 60 * 1000 && timeUntilExpiry > 0) {
          await refreshTokens();
        }
      } catch (error) {

      }
    };

    // Check immediately
    checkAndRefreshToken();

    // Set up interval to check every minute
    const interval = setInterval(checkAndRefreshToken, 60 * 1000);

    return () => clearInterval(interval);
  }, [state.platformAdmin.accessToken, state.isAuthenticated]);

  const contextValue: PlatformAdminContextType = {
    platformAdmin: state.platformAdmin,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    isInitialized: state.isInitialized,  // Sprint 17 P0 Fix
    error: state.error,
    setPlatformAdmin,
    logout,
    clearError,
    refreshTokens,
  };

  return (
    <PlatformAdminContext.Provider value={contextValue}>
      {children}
    </PlatformAdminContext.Provider>
  );
};

// Hook to use the context
export const usePlatformAdminAuth = (): PlatformAdminContextType => {
  const context = useContext(PlatformAdminContext);
  if (context === undefined) {
    throw new Error('usePlatformAdminAuth must be used within a PlatformAdminProvider');
  }
  return context;
};

// HOC for protected platform admin routes
export const withPlatformAdminAuth = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => {
    const { isAuthenticated, isLoading } = usePlatformAdminAuth();

    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
        </div>
      );
    }

    // Temporarily disable redirect for demo
    // if (!isAuthenticated) {
    //   // Redirect to platform admin login
    //   window.location.href = '/platform-admin/login';
    //   return null;
    // }

    return <Component {...props} />;
  };
};

export default PlatformAdminContext;